import { Checkbox, CheckTree, Dropdown, Icon, List, SearchBox } from 'tea-component';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'ahooks';
import type { IOptionType } from '@dbl/core/dataSource';
import { checkNullString, useDblIntl } from '@/pages/datalab/utils/utils';
import { getLanguage } from '@home/utils/common';
import { buildText } from '../../utils';

export function ListCheckbox(props: {
  className: string;
  selectName: string;
  selectValues: any[];
  options: IOptionType[];
  onChange: (values: string[], text?: string) => void;
  onOptionLoad: () => Promise<IOptionType[]>;
  onOptionResult: (options: IOptionType[]) => void;
}) {
  const { className, selectValues, onChange, options, onOptionLoad, onOptionResult } = props;
  const { commonFormatMessage, isEn } = useDblIntl("");

  const [selectManually, setSelectManually] = useState(false);
  const [columnText, setColumnText] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [checkedValue, setCheckedValue] = useState<string[]>([]);
  const [loading, setLoading] = useState(options.length === 0);
  const debounceSearch = useDebounce(searchValue, { wait: 300 });
  const lastNotifiedValueRef = useRef<string>(JSON.stringify([]));

  const optionOfAll = { value: 'all', text: commonFormatMessage("all") };
  const optionAll = useMemo<IOptionType[]>(() => {
    let newOptions: IOptionType[] = [];
    if (options.length === 0) {
      return newOptions;
    }
    if (checkNullString(debounceSearch)) {
      newOptions = [optionOfAll, ...options];
    } else {
      const searchLower = debounceSearch.toLowerCase();
      newOptions = options.filter((items) => {
        const textCn = (items.text ?? '').toLowerCase();
        const textEn = (items.text_en ?? '').toLowerCase();
        return textCn.includes(searchLower) || textEn.includes(searchLower);
      });
    }
    return newOptions;
  }, [JSON.stringify(options), debounceSearch]);

  useEffect(() => {
    if (options.length === 0) {
      setSearchValue('');
      setLoading(true);
      (async function () {
        const optionAll = await onOptionLoad();
        onOptionResult(optionAll);
        setLoading(false);
      })();
    } else {
      let columnValues = [];
      if (selectValues.length === 0 && selectManually === false) {
        columnValues = options.map((item: any) => item.value);
      } else {
        columnValues = options.filter((item) => selectValues.includes(item.value)).map((item) => item.value);
      }
      setCheckedValue(columnValues);
    }
  }, [JSON.stringify(options), JSON.stringify(selectValues)]);

  useEffect(() => {
    if (options.length === 0) {
      return;
    }
    const checkedValueKey = JSON.stringify(checkedValue);
    if (checkedValueKey === lastNotifiedValueRef.current) {
      return;
    }
    lastNotifiedValueRef.current = checkedValueKey;
    const text = buildText(checkedValue, options, isEn);
    setColumnText(text);
    onChange(checkedValue, text);
  }, [JSON.stringify(checkedValue), JSON.stringify(options)]);

  // 「全部」选项和其余选项是数关系
  const relations: any = {};
  if (optionOfAll) {
    for (const option of options) {
      if (
        // 用户可以指定哪些选项从全选逻辑中排除
        option.disabled
      ) {
        // continue
      } else {
        relations[option.value] = optionOfAll.value;
      }
    }
  }

  return (
    <Dropdown
      className={`dbl-list-checkbox-dropdown ${className}`}
      overlayClassName="dbl-list-checkbox-dropdown-overlay"
      appearance="button"
      button={columnText}
      clickClose={false}
    >
      {
        <div className="search-all-select">
          <CheckTree
            relations={relations}
            value={checkedValue}
            onChange={(values) => {
              setSelectManually(true);
              setCheckedValue([...values]);
            }}
          >
            <div className="dropdownCustom__search all-search">
              <SearchBox
                value={searchValue}
                placeholder={commonFormatMessage("search")}
                onChange={(value) => {
                  setSearchValue(value);
                }}
                onClear={() => {
                  setSearchValue('');
                }}
              />
            </div>
            <div className="dropdownCustom__bd">
              <List type="option" className="tea-list--checkoption">
                {optionAll.map((option: IOptionType, index: number) => {
                  const content =
                    getLanguage(isEn, option.text_en, option.text) || option.value;
                  return (
                    <List.Item key={index}>
                      <Checkbox onClick={(evt) => evt.stopPropagation()} name={option.value} title={content}>
                        {content}
                      </Checkbox>
                    </List.Item>
                  );
                })}
              </List>
              {loading ? (
                <div style={{ height: '20px', padding: '9px', boxSizing: 'content-box' }}>
                  <Icon type="loading" style={{ marginRight: 3 }} />
                  {commonFormatMessage("loading")}
                </div>
              ) : optionAll.length === 0 ? (
                <div className="block" style={{ minHeight: '149px', textAlign: 'center', lineHeight: '149px' }}>
                  {commonFormatMessage("no_data")}
                </div>
              ) : (
                ''
              )}
            </div>
          </CheckTree>
        </div>
      }
    </Dropdown>
  );
}
